import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fullRouteTo, goBack, routeTo, routeToWithOptions } from './routing.actions';
import { isStringOrNumber, validArray } from 'src/app/shared/utilities/types.utils';

import { AppState } from 'src/app/app.state';
import { ClientConfigurationState } from '../client-configuration/client-configuration.state';
import { Inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectClientConfiguration } from '../client-configuration/client-configuration.selectors';
import { map, tap } from 'rxjs/operators';
import { setLanguage } from '../client-configuration/client-configuration.actions';
import { WINDOW } from 'src/app/feature/injection-token/window';
import { getRouteParamsFromUrl } from 'src/app/shared/utilities/window.utils';
import { apiResponse } from '../api/api.actions';
import { isApi, isResponseOK } from '../api/api.utilities';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';

@Injectable({
	providedIn: 'root',
})
export class RoutingEffects {
	tenant: string;
	merchantId: string;
	language: string;

	/**
	 * Handles routing to a different state.
	 */
	routeTo$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(routeTo),
				tap((action) => {
					this.router.navigate([
						this.tenant,
						this.merchantId,
						this.language,
						...action.payload.filter(isStringOrNumber),
					]);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Handles the ability to route with specific options.
	 */
	routeToWithOptions$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(routeToWithOptions),
				tap((action) => {
					let { routeParams } = action;
					routeParams = routeParams.filter(isStringOrNumber);

					if (!validArray(routeParams) || (routeParams?.length === 1 && routeParams[0] === '/')) {
						routeParams = ['account'];
					}

					this.router.navigate([this.tenant, this.merchantId, this.language, ...routeParams], action.options);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Updates the URL with the new locale
	 */
	languageChange$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(apiResponse),
				map((action) => {
					// If we need to update the URL because they are switching locales
					if (
						!action.isOk ||
						!isApi(action, ApiRequestType.GET_APPLICATION_LOCALE) ||
						!action.customData?.updateURL
					) {
						return;
					}
					let pathParts = getRouteParamsFromUrl(this.getWindow());

					if (pathParts?.length > 3) {
						pathParts = pathParts.slice(3);
					}

					this.store.dispatch(
						routeToWithOptions({
							options: {
								replaceUrl: true,
								queryParamsHandling: 'preserve',
							},
							routeParams: pathParts,
						})
					);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Does a full replace of the entire url.
	 */
	fullRouteTo$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fullRouteTo),
				tap((action) => {
					let { payload } = action;
					payload = payload.filter(isStringOrNumber);

					if (validArray(payload)) {
						this.router.navigate(payload);
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * Goes back a page.
	 */
	goBack$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(goBack),
				tap(() => this.location.back())
			),
		{ dispatch: false }
	);

	/**
	 * @param window The window object
	 * @param actions$ The action stream.
	 * @param store The store
	 * @param router The router service
	 * @param location The location service
	 */
	constructor(
		@Inject(WINDOW) private window: Window,
		private actions$: Actions,
		private store: Store<AppState>,
		private router: Router,
		private location: Location
	) {
		store.select(selectClientConfiguration).subscribe((client: ClientConfigurationState) => {
			this.tenant = client.tenantName;
			this.merchantId = client.merchantId;
			this.language = client.language;
		});
	}

	/**
	 * Returns the window object
	 */
	getWindow(): Window {
		return this.window;
	}
}
